import React, { useState } from 'react';
import { TrendingDown, BarChart3, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { obrasApi, contactsApi } from '@/core/adapter-api';
import { StatusBadge, StatsGrid, EmptyState } from './shared';

// ─── Donut Chart ──────────────────────────────────────────────────────────────

const DONUT_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6'];

const DonutChart: React.FC<{ segments: { label: string; value: number; color: string }[]; centerLabel?: string; centerValue?: string }> = ({
  segments, centerLabel, centerValue
}) => {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;
  const size = 160;
  const r = 58;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth={18} />
          {segments.map((seg, i) => {
            const pct = seg.value / total;
            const dash = pct * circ;
            const el = (
              <circle
                key={i} cx={size/2} cy={size/2} r={r} fill="none"
                stroke={seg.color} strokeWidth={18}
                strokeDasharray={`${dash} ${circ - dash}`}
                strokeDashoffset={-offset}
                className="transition-all duration-700"
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        {centerLabel && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold text-white">{centerValue}</span>
            <span className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{centerLabel}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {segments.map((seg, i) => {
          const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
          return (
            <div key={i} className="flex items-center gap-2 group cursor-default">
              <div className="w-3 h-3 rounded-sm group-hover:scale-125 transition-transform" style={{ backgroundColor: seg.color }} />
              <span className="text-xs text-gray-400 w-24">{seg.label}</span>
              <span className="text-xs font-bold text-gray-200">{(seg.value / 1000).toFixed(0)}k€</span>
              <span className="text-[10px] text-gray-600">({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

interface CostCategory {
  id: string;
  name: string;
  spent: number;
  budget: number;
  status: string;
  subcategories: { name: string; amount: number }[];
}

function truncate(str: string, max: number) {
  return str.length > max ? str.slice(0, max) + '...' : str;
}

export const CostesRealesPage: React.FC = () => {
  const rawObras = useQuery(() => obrasApi.all(), [], ['obras']) || [];
  const rawContacts = useQuery(() => contactsApi.all(), [], ['contacts']) || [];
  
  const rawLogs = useQuery(async () => {
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('time_logs');
  }, [], ['time_logs']) || [];

  const rawExpenses = useQuery(async () => {
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('expenses');
  }, [], ['expenses']) || [];

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  const totalObraBudget = rawObras.reduce((s, o) => s + o.budget, 0);

  // 1. Calculate Mano de Obra (TimeLogs * HourlyRate)
  let moSpent = 0;
  rawLogs.forEach(log => {
    if (log.endTime) {
      const hours = (log.endTime - log.startTime) / (1000 * 60 * 60);
      const worker = rawContacts.find(c => c.id === log.contactId);
      if (worker && (worker as any).hourlyRate) {
        moSpent += hours * (worker as any).hourlyRate;
      }
    }
  });

  // 2. Calculate Materiales
  const matExpenses = rawExpenses.filter(e => (e as any).category?.toLowerCase() === 'materiales' || (e as any).category?.toLowerCase() === 'material');
  const matSpent = matExpenses.reduce((s, e) => s + ((e as any).baseTotal || 0), 0);

  // 3. Calculate Subcontratas
  const subExpenses = rawExpenses.filter(e => (e as any).category?.toLowerCase() === 'subcontratas' || (e as any).category?.toLowerCase() === 'subcontrata');
  const subSpent = subExpenses.reduce((s, e) => s + ((e as any).baseTotal || 0), 0);

  // 4. Calculate Otros Gastos
  const otherExpenses = rawExpenses.filter(e => {
    const cat = (e as any).category?.toLowerCase() || '';
    return cat !== 'materiales' && cat !== 'material' && cat !== 'subcontratas' && cat !== 'subcontrata';
  });
  const othSpent = otherExpenses.reduce((s, e) => s + ((e as any).baseTotal || 0), 0);

  const moBudget = totalObraBudget * 0.4;
  const matBudget = totalObraBudget * 0.4;
  const subBudget = totalObraBudget * 0.15;
  const othBudget = totalObraBudget * 0.05;

  const costs: CostCategory[] = [
    {
      id: '1', name: 'Mano de obra', spent: Math.round(moSpent), budget: Math.round(moBudget), status: moSpent > moBudget ? 'Desviado' : 'Activo',
      subcategories: [{ name: 'Jornadas registradas', amount: Math.round(moSpent) }]
    },
    {
      id: '2', name: 'Materiales', spent: Math.round(matSpent), budget: Math.round(matBudget), status: matSpent > matBudget ? 'Desviado' : 'Activo',
      subcategories: matExpenses.slice(0, 5).map(e => ({ name: truncate((e as any).category || 'Gasto', 20), amount: (e as any).baseTotal || 0 }))
    },
    {
      id: '3', name: 'Subcontratas', spent: Math.round(subSpent), budget: Math.round(subBudget), status: subSpent > subBudget ? 'Desviado' : 'Activo',
      subcategories: subExpenses.slice(0, 5).map(e => ({ name: truncate((e as any).category || 'Gasto', 20), amount: (e as any).baseTotal || 0 }))
    },
    {
      id: '4', name: 'Otros gastos', spent: Math.round(othSpent), budget: Math.round(othBudget), status: othSpent > othBudget ? 'Desviado' : 'Pendiente',
      subcategories: otherExpenses.slice(0, 5).map(e => ({ name: truncate((e as any).category || 'Gasto', 20), amount: (e as any).baseTotal || 0 }))
    }
  ];

  const totalSpent = costs.reduce((s, c) => s + c.spent, 0);
  const totalBudget = costs.reduce((s, c) => s + c.budget, 0);
  const deviation = totalBudget > 0 ? ((totalSpent / totalBudget - 1) * 100).toFixed(1) : '0';
  const margin = totalBudget > 0 ? ((1 - totalSpent / totalBudget) * 100).toFixed(0) : '0';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl">
            <TrendingDown size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Costes Reales</h1>
            <p className="text-sm text-gray-500">Análisis de costes reales vs. presupuestados</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const headers = ['Categoría', 'Gastado (€)', 'Presupuesto (€)', 'Estado'];
              const rows = costs.map(c => [c.name, String(c.spent), String(c.budget), c.status]);
              rows.push(['TOTAL', String(totalSpent), String(totalBudget), `Desviación: ${deviation}%`]);
              const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `costes_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-[#0d1117] border border-white/10 rounded-xl text-sm text-gray-300 hover:border-amber-500/50 transition-colors"
            title="Exportar CSV"
          >
            <Download size={14} /> CSV
          </button>
          <button
            onClick={() => setShowComparison(!showComparison)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              showComparison
                ? 'bg-amber-600 text-white'
                : 'bg-[#0d1117] border border-white/10 text-gray-300 hover:border-amber-500/50'
            }`}
          >
            <BarChart3 size={16} />
            {showComparison ? 'Ocultar comparativa' : 'Ver comparativa'}
          </button>
        </div>
      </div>

      <StatsGrid stats={[
        { label: 'Coste total', value: `${(totalSpent / 1000).toFixed(0)}k€` },
        { label: 'Presupuestado', value: `${(totalBudget / 1000).toFixed(0)}k€` },
        { label: 'Desviación', value: `${deviation}%`, color: parseFloat(deviation) <= 0 ? 'text-emerald-400' : 'text-red-400' },
        { label: 'Margen previsto', value: `${margin}%`, color: 'text-amber-400' },
      ]} />

      {rawObras.length === 0 ? (
        <EmptyState
          icon={TrendingDown}
          title="Sin datos de costes"
          description="Crea tu primera obra con presupuesto para empezar a analizar costes reales vs. presupuestados."
        />
      ) : (
      <>
      {/* Donut Distribution Chart */}
      <div className="bg-[#0d1117] border border-white/8 rounded-xl p-6 mb-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-5">Distribución de Costes</h3>
        <div className="flex justify-center">
          <DonutChart
            segments={costs.map((c, i) => ({ label: c.name, value: c.budget, color: DONUT_COLORS[i] }))}
            centerLabel="Presupuesto"
            centerValue={`${(totalBudget / 1000).toFixed(0)}k€`}
          />
        </div>
      </div>

      {showComparison && (
        <div className="bg-[#0d1117] border border-white/8 rounded-xl p-5 mb-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">Comparativa Real vs. Presupuesto</h3>
          <div className="space-y-4">
            {costs.map((cat) => {
              const pct = cat.budget > 0 ? Math.round((cat.spent / cat.budget) * 100) : 0;
              const overBudget = pct > 100;
              return (
                <div key={cat.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-gray-300">{cat.name}</span>
                    <span className="text-xs text-gray-500">
                      {(cat.spent / 1000).toFixed(0)}k€ / {(cat.budget / 1000).toFixed(0)}k€
                      <span className={`ml-2 font-bold ${overBudget ? 'text-red-400' : 'text-emerald-400'}`}>
                        ({pct}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden relative">
                    <div className="absolute inset-0 border-r-2 border-dashed border-gray-600" style={{ width: '100%' }} />
                    <div
                      className={`h-full rounded-full transition-all ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/8">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Desglose por categoría</h3>
        </div>
        <div className="divide-y divide-white/5">
          {costs.map((cat) => (
            <div key={cat.id}>
              <div
                className={`flex items-center justify-between px-5 py-3 cursor-pointer transition-colors ${
                  expandedId === cat.id ? 'bg-amber-500/5' : 'hover:bg-white/3'
                }`}
                onClick={() => setExpandedId(expandedId === cat.id ? null : cat.id)}
              >
                <div className="flex items-center gap-3">
                  <button className="text-gray-500">
                    {expandedId === cat.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <div>
                    <p className="text-sm text-gray-200 font-medium">{cat.name}</p>
                    <p className="text-xs text-gray-500">
                      {(cat.spent / 1000).toFixed(1)}k€ de {(cat.budget / 1000).toFixed(0)}k€ presupuestados
                    </p>
                  </div>
                </div>
                <StatusBadge status={cat.status} />
              </div>

              {expandedId === cat.id && (
                <div className="px-5 pb-4 bg-[#0a0e14] border-t border-white/5">
                  <div className="pt-3 space-y-2">
                    {cat.subcategories.map((sub, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5">
                        <span className="text-sm text-gray-400">{sub.name}</span>
                        <span className="text-sm font-semibold text-gray-200">{sub.amount.toLocaleString('es-ES')}€</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                      <span className="text-sm font-bold text-gray-300">Total</span>
                      <span className="text-sm font-bold text-amber-400">
                        {cat.subcategories.reduce((s, sc) => s + sc.amount, 0).toLocaleString('es-ES')}€
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
  );
};
